import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import DocumentIcon from '~/icons/icon-document.svg?react';
import { useFixedAssetsSpeciesQuery } from '~/services/bookkeeping/fixed-assets-species';
import { FixedAssetResource } from '~/types/fixed-asset';
import { formatPercentageLimitingDecimals } from '~/utils/formatNumber';
import { formatMoney } from '~/utils/money';
import styles from './BookkeepingFixedAssetsPreview.module.scss';
import { PreviewField } from './PreviewField';

interface BookkeepingFixedAssetsPreviewProps {
  fixedAssets: FixedAssetResource[];
}

const BookkeepingFixedAssetsPreview = ({ fixedAssets }: BookkeepingFixedAssetsPreviewProps) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'general_bookkeeping.cash_book.preview.fixed_assets' });

  const { data: companySpecies = [] } = useFixedAssetsSpeciesQuery();

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const companySpecie of companySpecies) {
      for (const speciesCategory of companySpecie.species.speciesCategories ?? []) {
        if (speciesCategory.category) map.set(speciesCategory.id, speciesCategory.category.name);
      }
    }
    return map;
  }, [companySpecies]);

  if (fixedAssets.length === 0) {
    return null;
  }

  return (
    <VStack gap={1} data-testid="cash-book-tooltip-fixed-assets">
      <Text variant="caption" weight="semibold">
        {t('title')}
      </Text>

      {fixedAssets.map((fixedAsset) => {
        const category = fixedAsset.speciesCategory ? categoryNameById.get(fixedAsset.speciesCategory.id) : undefined;
        const depreciationRate =
          fixedAsset.bookDepreciationRate != null
            ? formatPercentageLimitingDecimals(Number(fixedAsset.bookDepreciationRate) * 100)
            : '-';
        const assetTypeLabel =
          fixedAsset.assetType === 'INTANGIBLE'
            ? t('asset_type_intangible')
            : fixedAsset.assetType === 'TANGIBLE'
              ? t('asset_type_tangible')
              : '-';

        return (
          <VStack key={fixedAsset.id} gap={1} className={styles.card}>
            <HStack gap={1} align="center">
              <Icon component={DocumentIcon} className={styles.cardIcon} />
              <Text variant="body2" weight="semibold">
                {fixedAsset.description ?? '-'}
              </Text>
            </HStack>

            <HStack gap={2} align="center">
              <PreviewField
                label={t('amount')}
                value={fixedAsset.initialBookValue ? formatMoney(fixedAsset.initialBookValue) : '-'}
              />
              <PreviewField label={t('category')} value={category ?? '-'} />
              <PreviewField label={t('depreciation_rate')} value={depreciationRate} />
              <PreviewField label={t('type')} value={assetTypeLabel} />
            </HStack>
          </VStack>
        );
      })}
    </VStack>
  );
};

export default BookkeepingFixedAssetsPreview;
